import React, { useEffect, useRef } from "react";
import CategorySelect from "./CategorySelect";
import searchIcon from '@/public/svg/search.svg';
import { categoryIcons, categoryImages } from '@/helpers/categoryImages';
import { InitData } from "@/models/customersTypes";
import fallbackImage from "@/public/img/categories/fallback-image.png";
import angeboteImage from "@/public/img/categories/angebote.png";

const priority = ["Gemüse", "Frische"];

export type CategorySource = 'dropdown' | 'tile';

interface FiltersProps {
    initData: InitData;
    selectedCategory: string;
    searchValue: string;
    hideSearchButton?: boolean;
    autoFocus?: boolean;
    focusKey?: string | number;
    onCategoryChange: (category: string, source: CategorySource) => void;
    onSearchChange: (value: string) => void;
    onSearchSubmit: () => void;
    onResetFilters: () => void;
}

const Filters: React.FC<FiltersProps> = ({
    initData,
    selectedCategory,
    searchValue,
    hideSearchButton = false,
    autoFocus = false,
    focusKey,
    onCategoryChange,
    onSearchChange,
    onSearchSubmit,
    onResetFilters
}) => {
    const inputRef = useRef<HTMLInputElement | null>(null);

    useEffect(() => {
        if (!autoFocus) return;
        inputRef.current?.focus();
    }, [autoFocus, focusKey]);

    const isSearchDisabled = searchValue === '' && selectedCategory === '';

    return (
        <>
            <div className="flex flex-col gap-4 md:pt-8">
                {/* categories */}
                <div className="relative w-full">
                    <CategorySelect
                        categories={categoryIcons}
                        value={selectedCategory}
                        onChange={(val) => {
                            onCategoryChange(val, 'dropdown');
                        }}
                    />
                </div>

                {/* search field */}
                <div className="relative w-full">
                    <span className="absolute inset-y-0 left-3 flex items-center text-gray-400 pointer-events-none">
                        <img src={searchIcon} alt="Search Icon" loading="lazy" />
                    </span>

                    <input
                        ref={inputRef}
                        type="text"
                        placeholder="Suche nach Produkten..."
                        value={searchValue}
                        onChange={(e) => onSearchChange(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' ? onSearchSubmit() : null}
                        className="pl-9 pr-3 py-2 border border-gray-300 rounded-md w-full focus:outline-none focus:ring-0 focus:border-gray-400"
                    />
                </div>

                {!hideSearchButton && (
                    <button
                        onClick={() => onSearchSubmit()}
                        disabled={isSearchDisabled}
                        className="px-4 py-2 bg-primary text-gray-700 rounded-full hover:bg-primaryDark transition whitespace-nowrap"
                    >
                        Suchen
                    </button>
                )}

                <button
                    onClick={onResetFilters}
                    className="px-4 py-2 bg-gray-200 text-gray-700 rounded-full hover:bg-gray-300 transition whitespace-nowrap"
                >
                    Filter zurücksetzen
                </button>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-2 gap-4 mb-16">
              <button
              key={"angebote"}
              onClick={() => {
                onCategoryChange("angebote", 'tile');
                window.scrollTo({ top: 0, behavior: "smooth" }); }}
              className={`bg-white rounded-xl shadow hover:shadow-lg transition p-2 text-center flex flex-col border-2 ${selectedCategory == "" ? "border-secondary" : "border-transparent"}`}>
              <img src={angeboteImage} alt={"angebote"} className="rounded-md mb-2 object-cover w-full h-16 sm:h-12 md:h-14 lg:h-20" />
              <span className="text-md font-semibold">{"Angebote"}</span>
              </button>

                {initData?.categories
                    ?.slice()
                    .sort((a, b) => {
                        const indexA = priority.indexOf(a.name);
                        const indexB = priority.indexOf(b.name);

                        if (indexA !== -1 && indexB !== -1) return indexA - indexB;
                        if (indexA !== -1) return -1;
                        if (indexB !== -1) return 1;
                        return a.name.localeCompare(b.name);
                    })
                    .map((category) => {
                        const cat = categoryImages.find(c => c.name === category.name);
                        const image = cat?.image ?? fallbackImage;
                        const isSelected = selectedCategory === category.name;

                        return (
                            <button
                                key={category.id}
                                onClick={() => {
                                    onCategoryChange(category.name, 'tile');
                                    window.scrollTo({ top: 0, behavior: "smooth" });
                                }}
                                className={`bg-white rounded-xl shadow hover:shadow-lg transition p-2 text-center flex flex-col border-2 ${isSelected ? "border-secondary" : "border-transparent"}`}
                            >
                                <img
                                    src={image}
                                    alt={category.name}
                                    className="rounded-md mb-2 object-cover w-full h-16 sm:h-12 md:h-14 lg:h-20"
                                />
                                <span className="text-md font-semibold">{category.name}</span>
                            </button>
                        );
                    })}
            </div>
        </>
    );
};

export default Filters;
